import React from 'react';
import { Typography, Breadcrumb, Space, Card, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { colors, spacing, shadows, createFlexBetween } from '../../styles/theme';
import ErrorBoundary from './ErrorBoundary';

const { Title, Text } = Typography;

interface BreadcrumbItem {
  title: string;
  path?: string;
  onClick?: () => void;
}

interface PageAction {
  key: string;
  label: string;
  type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
  icon?: React.ReactNode;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
  danger?: boolean;
}

interface PageLayoutProps {
  /** 페이지 제목 */
  title: string;
  /** 페이지 설명 */
  subtitle?: string;
  /** 브레드크럼 항목들 */
  breadcrumb?: BreadcrumbItem[];
  /** 페이지 액션 버튼들 */
  actions?: PageAction[];
  /** 뒤로가기 버튼 표시 여부 */
  showBackButton?: boolean;
  /** 뒤로가기 핸들러 */
  onBack?: () => void;
  /** 페이지 헤더 추가 콘텐츠 */
  extra?: React.ReactNode;
  /** 자식 컴포넌트들 */
  children: React.ReactNode;
  /** 로딩 상태 */
  loading?: boolean;
  /** 에러 상태 */
  error?: string | null;
  /** 컨텐츠 패딩 */
  contentPadding?: boolean;
  /** 페이지 최대 너비 */
  maxWidth?: number;
  /** 헤더 배경색 */
  headerBackground?: string;
}

/**
 * 통합 페이지 레이아웃 컴포넌트
 * 
 * 일관된 페이지 구조와 스타일을 제공하는 레이아웃 컴포넌트입니다.
 * 제목, 브레드크럼, 액션 버튼, 에러 처리 등의 기능을 포함합니다.
 */
const PageLayout: React.FC<PageLayoutProps> = React.memo(({
  title,
  subtitle,
  breadcrumb,
  actions = [],
  showBackButton = false,
  onBack,
  extra,
  children,
  loading = false,
  error = null,
  contentPadding = true,
  maxWidth,
  headerBackground = colors.background.primary,
}) => {
  const handleBreadcrumbClick = (item: BreadcrumbItem) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.path) {
      // 라우터 네비게이션 (현재는 내부 상태 기반이므로 생략)
      console.log('Navigate to:', item.path);
    }
  };

  const renderBreadcrumb = () => {
    if (!breadcrumb?.length) return null;

    const breadcrumbItems = breadcrumb.map((item) => ({
      title: item.onClick || item.path ? (
        <Button 
          type="link" 
          size="small"
          onClick={() => handleBreadcrumbClick(item)}
          style={{ padding: 0, height: 'auto' }}
        >
          {item.title}
        </Button>
      ) : (
        <Text>{item.title}</Text>
      ),
    }));

    return (
      <Breadcrumb 
        items={breadcrumbItems}
        style={{ marginBottom: spacing[2] }}
      />
    );
  };

  const renderActions = () => {
    if (!actions.length) return null;

    return (
      <Space size="small">
        {actions.map((action) => (
          <Button
            key={action.key}
            type={action.type || 'default'}
            icon={action.icon}
            onClick={action.onClick}
            loading={action.loading}
            disabled={action.disabled}
            danger={action.danger}
          >
            {action.label}
          </Button>
        ))}
      </Space>
    );
  };

  const pageStyle: React.CSSProperties = {
    minHeight: '100%',
    backgroundColor: colors.background.secondary,
  };

  const containerStyle: React.CSSProperties = {
    maxWidth: maxWidth || '100%',
    margin: '0 auto',
    padding: contentPadding ? spacing[4] : 0,
  };

  const headerStyle: React.CSSProperties = {
    backgroundColor: headerBackground,
    padding: spacing[4],
    marginBottom: spacing[4],
    borderRadius: 8,
    boxShadow: shadows.sm,
  };

  const contentStyle: React.CSSProperties = {
    backgroundColor: colors.background.primary,
    borderRadius: 8,
    boxShadow: shadows.card,
    ...(contentPadding && { padding: spacing[6] }),
  };

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={containerStyle}>
          <Card style={headerStyle}>
            <div style={createFlexBetween()}>
              <div>
                {showBackButton && (
                  <Button 
                    type="text" 
                    icon={<ArrowLeftOutlined />} 
                    onClick={onBack}
                    style={{ marginBottom: spacing[2] }}
                  >
                    뒤로
                  </Button>
                )}
                {renderBreadcrumb()}
                <Title level={2} style={{ margin: 0 }}>
                  {title}
                </Title>
              </div>
            </div>
          </Card>
          
          <Card style={{ textAlign: 'center', padding: spacing[8] }}>
            <Text type="danger" style={{ fontSize: 16 }}>
              {error}
            </Text>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div style={pageStyle}>
        <div style={containerStyle}>
          {/* 페이지 헤더 */}
          <Card style={headerStyle}>
            <div style={createFlexBetween()}>
              <div style={{ flex: 1 }}>
                {showBackButton && (
                  <Button 
                    type="text" 
                    icon={<ArrowLeftOutlined />} 
                    onClick={onBack}
                    style={{ marginBottom: spacing[2] }}
                  >
                    뒤로
                  </Button>
                )}
                {renderBreadcrumb()}
                
                <div style={createFlexBetween()}>
                  <div>
                    <Title level={2} style={{ margin: 0, marginBottom: spacing[1] }}>
                      {title}
                    </Title>
                    {subtitle && (
                      <Text type="secondary" style={{ fontSize: 16 }}>
                        {subtitle}
                      </Text>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: spacing[4] }}>
                    {extra}
                    {renderActions()}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* 페이지 콘텐츠 */}
          <div style={contentStyle}>
            {loading ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                minHeight: 400,
              }}>
                <Text>로딩 중...</Text>
              </div>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
});

PageLayout.displayName = 'PageLayout';

export default PageLayout;